<template>
  <div class="list-card" :class="{ 'is-bundle': isBundle || isGrouped }" @click="navigate">

    <!-- Afbeelding links -->
    <div class="list-image">
      <!-- LQIP: geblurde 32px-placeholder, fade weg als de scherpe foto er is -->
      <img
        v-if="lqipSrc"
        :src="lqipSrc"
        alt=""
        aria-hidden="true"
        class="lqip"
        :class="{ hidden: imgLoaded }"
      >
      <img
        ref="mainImgEl"
        :src="productImage"
        :alt="product.name"
        :loading="index < 4 ? 'eager' : 'lazy'"
        class="main-img"
        @load="imgLoaded = true"
        @error="imgLoaded = true"
      >
      <!-- Bundle strip -->
      <div v-if="isBundle || isGrouped" class="list-bundle-strip">
        <i class="bi bi-check2-circle"></i> Complete set
      </div>
    </div>

    <!-- Inhoud midden -->
    <div class="list-body">

      <!-- Categorie -->
      <span v-if="productCategory" class="list-category">{{ productCategory }}</span>

      <!-- Titel -->
      <h3 class="list-title">{{ product.name }}</h3>

      <!-- USPs -->
      <ul class="list-usps">
        <template v-if="productUsps">
          <li v-for="usp in productUsps.slice(0, 3)" :key="usp">
            <i class="bi bi-check2"></i> {{ usp }}
          </li>
        </template>
        <template v-else>
          <li><i class="bi bi-check2"></i> Morgen in huis</li>
          <li><i class="bi bi-check2"></i> 2 jaar garantie</li>
          <li v-if="hasFreeShipping"><i class="bi bi-check2"></i> Gratis verzending</li>
        </template>
      </ul>

      <!-- Prijs -->
      <div class="list-price-row">
        <span v-if="isGrouped" class="list-price-from">Vanaf</span>
        <span class="list-price">{{ formatPrice(currentPrice) }}</span>
        <span v-if="!isGrouped && !isBundle && product.on_sale && regularPrice" class="list-price-old">{{ formatPrice(regularPrice) }}</span>
        <span v-if="!isGrouped && !isBundle && savingsPercentage > 0" class="list-savings">-{{ savingsPercentage }}%</span>
        <span v-if="isGrouped || isBundle" class="list-set-pill">Complete set</span>
      </div>

      <!-- Excl. BTW -->
      <div v-if="!isGrouped && !isBundle && currentPrice && !isZeroVat" class="list-excl-btw">
        Excl. BTW: {{ formatPrice(exclBtwPrice) }}
      </div>

      <!-- Levertijd -->
      <div v-if="deliveryTime" class="list-delivery" :style="`color:${deliveryTime.color}`">
        <i :class="['bi', deliveryTime.icon]"></i>
        {{ deliveryTime.label }}
        <span v-if="deliveryTime.sub" class="list-delivery-sub">· {{ deliveryTime.sub }}</span>
      </div>

    </div>

    <!-- Rechterkant: badges + knop -->
    <div class="list-right">

      <!-- Badges -->
      <div class="list-badges">
        <span v-if="bestseller" class="badge bestseller-badge"><i class="bi bi-fire"></i> Bestseller</span>
        <span v-if="isBundle || isGrouped" class="badge bundle-badge"><i class="bi bi-box-seam"></i> Set</span>
        <span v-if="product.on_sale && savingsPercentage > 0" class="badge sale-badge">-{{ savingsPercentage }}%</span>
        <span v-if="isNewProduct" class="badge new-badge">Nieuw</span>
        <span v-if="isLowStock" class="badge stock-badge"><i class="bi bi-fire"></i> {{ stockQuantity }} over</span>
      </div>

      <!-- Op voorraad indicator -->
      <div v-if="(product.stock_status === 'instock' || product.backorders_allowed) && !isLowStock && !isBundle && !isGrouped" class="list-in-stock">
        <span class="list-stock-dot">●</span> Op voorraad
      </div>

      <!-- Uitverkocht: voorraad-melding (compact) -->
      <div v-if="product.stock_status === 'outofstock' && !product.backorders_allowed && !isBundle && !isGrouped" @click.stop class="w-full">
        <StockNotify
          :productId="product.id"
          :productName="product.name"
          :productSlug="product.slug"
          :productImage="product.thumbnail || product.images?.[0]?.src || ''"
          :compact="true"
        />
      </div>

      <!-- Knop (op voorraad of bundle/grouped) -->
      <button
        v-else
        @click.stop="addToCart"
        class="list-btn"
        :disabled="isAddingToCart"
      >
        <i :class="['bi', isAddingToCart ? 'bi-arrow-repeat spin' : (isBundle || isGrouped) ? 'bi-arrow-right-circle' : 'bi-cart']"></i>
        <span class="list-btn-label">{{ addToCartText }}</span>
      </button>

      <!-- Wishlist -->
      <button class="list-wishlist" :class="{ active: isInWishlist }" @click.stop="toggleWishlist">
        <i :class="['bi', isInWishlist ? 'bi-heart-fill' : 'bi-heart']"></i>
      </button>

    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useCartStore } from '@stores/cart'
import { useWishlistStore } from '@stores/wishlist'
import { useRouter } from 'vue-router'
import StockNotify from '@components/StockNotify.vue'
import { useAnalytics } from '@composables/useAnalytics'

const props = defineProps({
  product:  { type: Object, required: true },
  index:    { type: Number, default: 99 },
  listName: { type: String, default: 'Productoverzicht' },
  bestseller: { type: Boolean, default: false },
})

const cartStore     = useCartStore()
const wishlistStore = useWishlistStore()
const router        = useRouter()
const { trackSelectItem, trackAddToCart, trackWishlist } = useAnalytics()
const isAddingToCart = ref(false)
const deliveryTime   = ref(null)
const imgLoaded      = ref(false)
const mainImgEl      = ref(null)

onMounted(async () => {
  // Afbeelding kan al geladen zijn vóór hydratie → dan is @load al gemist.
  if (mainImgEl.value?.complete) imgLoaded.value = true
  try {
    const res = await fetch(`/_delivery-time/${props.product.id}`)
    if (res.ok) deliveryTime.value = await res.json()
  } catch {}
})

const isInWishlist = computed(() => wishlistStore.isInWishlist(props.product.id))

const productImage    = computed(() => props.product.images?.[0]?.src || props.product.thumbnail || '/placeholder-product.jpg')
// LQIP via de bestaande media-proxy (?w=32); alleen voor geproxyde /wp-content-URLs.
const lqipSrc = computed(() => {
  const src = productImage.value
  if (!src || !src.startsWith('/wp-content/')) return ''
  return src.includes('?') ? `${src}&w=32` : `${src}?w=32`
})
const productCategory = computed(() => props.product.categories?.[0]?.name || null)
const productUsps     = computed(() => props.product.usps || null)
const isGrouped       = computed(() => props.product.type === 'grouped')
const isBundle        = computed(() => props.product.is_bundle === true)
const currentPrice    = computed(() => props.product.sale_price || props.product.price || props.product.regular_price)
const regularPrice    = computed(() => props.product.regular_price)
const hasFreeShipping = computed(() => parseFloat(currentPrice.value) >= 50)
const stockQuantity   = computed(() => props.product.stock_quantity || 0)
const isZeroVat       = computed(() => props.product.tax_class === 'zero-rate')
const exclBtwPrice    = computed(() => currentPrice.value ? parseFloat(currentPrice.value) / 1.21 : 0)
const isLowStock      = computed(() => props.product.stock_status === 'instock' && stockQuantity.value > 0 && stockQuantity.value <= 3)

const isNewProduct = computed(() => {
  if (!props.product.date_created) return false
  return Math.floor((Date.now() - new Date(props.product.date_created)) / 86400000) <= 30
})

const savingsAmount = computed(() => {
  if (props.product.on_sale && regularPrice.value && currentPrice.value)
    return parseFloat(regularPrice.value) - parseFloat(currentPrice.value)
  return 0
})

const savingsPercentage = computed(() => {
  if (savingsAmount.value > 0 && regularPrice.value)
    return Math.round((savingsAmount.value / parseFloat(regularPrice.value)) * 100)
  return 0
})

const addToCartText = computed(() => {
  if (isGrouped.value || isBundle.value) return 'Bekijk set'
  if (isAddingToCart.value) return 'Laden...'
  if (props.product.stock_status === 'outofstock' && !props.product.backorders_allowed) return 'Uitverkocht'
  return 'In winkelwagen'
})

import { formatPrice } from '@/utils/price'

const navigate = () => {
  trackSelectItem(props.product, props.listName, props.index)
  router.push(`/product/${props.product.slug}`)
}

const addToCart = async () => {
  if (isGrouped.value || isBundle.value) return navigate()
  if (isAddingToCart.value || (props.product.stock_status === 'outofstock' && !props.product.backorders_allowed)) return
  try {
    isAddingToCart.value = true
    await cartStore.addToCart(props.product, 1)
    trackAddToCart(props.product, 1, 'list-card')
  } finally {
    isAddingToCart.value = false
  }
}

const toggleWishlist = () => {
  const inWishlist = wishlistStore.isInWishlist(props.product.id)
  wishlistStore.toggleWishlist(props.product)
  trackWishlist(props.product, inWishlist ? 'remove' : 'add')
}
</script>

<style scoped>
/* ── Card shell ── */
.list-card {
  display: flex;
  align-items: stretch;
  background: #fff;
  border: 1px solid #e9eaec;
  border-radius: 14px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 150ms cubic-bezier(0.23, 1, 0.32, 1),
              border-color 150ms cubic-bezier(0.23, 1, 0.32, 1);
}

.list-card:hover {
  transform: translateY(-2px);
  border-color: #FFD500;
}

.list-card.is-bundle {
  border-color: #FFD500;
}

/* ── Afbeelding ── */
.list-image {
  position: relative;
  width: 88px;
  min-width: 88px;
  height: 88px;
  align-self: center;
  margin: 10px;
  border-radius: 10px;
  background: #f7f7f7;
  flex-shrink: 0;
  overflow: hidden;
}

.list-image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 8px;
}

/* LQIP blur-up */
.list-image .lqip {
  position: absolute;
  inset: 0;
  z-index: 1;
  filter: blur(12px);
  transform: scale(1.06);
  opacity: 1;
  transition: opacity 180ms cubic-bezier(0.23, 1, 0.32, 1);
}
.list-image .lqip.hidden {
  opacity: 0;
}
.list-image .main-img {
  position: relative;
  z-index: 2;
}

.list-bundle-strip {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: #FFD500;
  color: #111;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 3px 8px;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* ── Body midden ── */
.list-body {
  flex: 1;
  min-width: 0;
  padding: 12px 12px 12px 4px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.list-category {
  font-size: 0.65rem;
  font-weight: 600;
  color: #aaa;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.list-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #111;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.list-usps {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.list-usps li {
  font-size: 0.76rem;
  color: #555;
  display: flex;
  align-items: center;
  gap: 5px;
}

.list-usps li i {
  color: #22c55e;
  font-size: 0.78rem;
  flex-shrink: 0;
}

.list-price-row {
  display: flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: auto;
  padding-top: 8px;
}

.list-price-from {
  font-size: 0.72rem;
  color: #aaa;
  font-weight: 500;
}

.list-price {
  font-size: 1.15rem;
  font-weight: 700;
  color: #111;
  line-height: 1;
}

.list-price-old {
  font-size: 0.85rem;
  color: #bbb;
  text-decoration: line-through;
}

.list-savings {
  font-size: 0.68rem;
  font-weight: 700;
  background: #fff3cd;
  color: #856404;
  padding: 2px 6px;
  border-radius: 20px;
}

.list-excl-btw {
  font-size: 0.72rem;
  color: #999;
  margin-top: -4px;
}

.list-delivery {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.72rem;
  font-weight: 600;
  margin-top: 2px;
}
.list-delivery i { font-size: 0.78rem; }
.list-delivery-sub { font-weight: 400; color: #999; }

.list-in-stock {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.72rem;
  font-weight: 600;
  color: #16a34a;
}

.list-stock-dot {
  font-size: 0.55rem;
  line-height: 1;
}

.list-set-pill {
  font-size: 0.68rem;
  font-weight: 700;
  background: #111;
  color: #FFD500;
  padding: 2px 8px;
  border-radius: 20px;
}

/* ── Rechterkant ── */
.list-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
  padding: 12px 16px;
  gap: 8px;
  min-width: 140px;
  flex-shrink: 0;
}

/* Badges */
.list-badges {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 3px 8px;
  border-radius: 5px;
  font-size: 0.68rem;
  font-weight: 700;
  white-space: nowrap;
}

.sale-badge  { background: #ff4757; color: #fff; }
.new-badge   { background: #FFD500; color: #000; }
.bundle-badge { background: #111; color: #FFD500; }
.stock-badge { background: #fff3e0; color: #e65100; }
.bestseller-badge { background: #111; color: #FFD500; gap: 4px; }
.bestseller-badge i { font-size: 0.7rem; }

/* Knop */
.list-btn {
  width: 100%;
  background: #FFD500;
  color: #111;
  border: none;
  border-radius: 8px;
  padding: 9px 14px;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: background 150ms cubic-bezier(0.23, 1, 0.32, 1),
              transform 150ms cubic-bezier(0.23, 1, 0.32, 1);
  white-space: nowrap;
}

.list-btn:hover:not(:disabled) {
  background: #ffb800;
  transform: translateY(-1px);
}

.list-btn:active:not(:disabled) {
  transform: scale(0.98) translateY(0);
  background: #f0b800;
}

.list-btn:disabled {
  background: #e5e7eb;
  color: #9ca3af;
  cursor: not-allowed;
}

/* Wishlist */
.list-wishlist {
  background: none;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #bbb;
  font-size: 0.95rem;
  transition: color 0.15s, border-color 0.15s;
  flex-shrink: 0;
  align-self: flex-end;
}

.list-wishlist:hover, .list-wishlist.active { color: #FFD500; border-color: #FFD500; }

/* ── Mobiel: 2-rij layout ──
   Rij 1: [afbeelding 84px] [titel + prijs  flex-1]
   Rij 2: [badges verborgen] [cart-knop / meldknop flex-1] [wishlist 36px]
*/
@media (max-width: 640px) {
  .list-card  { flex-wrap: wrap; align-items: flex-start; }

  /* Rij 1 – links: behoud dezelfde mini-box stijl */
  .list-image {
    width: 72px;
    min-width: 72px;
    height: 72px;
    margin: 10px 8px;
    border-radius: 8px;
    align-self: flex-start;
  }

  /* Rij 1 – rechts */
  .list-body  {
    flex: 1;
    min-width: 0;
    padding: 10px 10px 10px 4px;
    gap: 3px;
  }

  /* Rij 2 – volledige breedte */
  .list-right {
    width: 100%;
    min-width: 0;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    padding: 6px 12px 10px;
    gap: 8px;
    border-top: 1px solid #f3f3f3;
  }

  /* StockNotify-wrapper neemt flex-ruimte in rij 2 */
  .list-right > .w-full { flex: 1; }

  /* Cart-knop: vullen wat er over is, tekst blijft leesbaar */
  .list-btn {
    width: auto;
    flex: 1;
    font-size: 0.8rem;
    padding: 7px 12px;
  }

  /* Badges weglaten op mobiel – te klein om nuttig te zijn */
  .list-badges    { display: none; }

  /* Verberg minder relevante body-elementen */
  .list-usps      { display: none; }
  .list-category  { display: none; }
  .list-price-old { display: none; }

  .list-title    { font-size: 0.85rem; }
  .list-price    { font-size: 1rem; }
  .list-wishlist { width: 34px; height: 34px; flex-shrink: 0; }
}

.spin { animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
</style>
